import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, EyeOff, Eye, Archive, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

export default function ModBygdepuls() {
  const token = sessionStorage.getItem("mod_token");
  const [items, setItems] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, dupRes] = await Promise.all([
        axios.get(`${API}/mod/bygdepuls`, { headers: headers() }),
        axios.get(`${API}/mod/bygdepuls/duplicates`, { headers: headers() }),
      ]);
      setItems(listRes.data || []);
      setDuplicates(dupRes.data?.groups || []);
    } catch (err) {
      toast.error(`Kunde inte läsa Bygdepuls: ${err.response?.data?.detail || err.message || "fel"}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const duplicateIds = useMemo(() => {
    const ids = new Set();
    duplicates.forEach((group) => (group.items || []).forEach((item) => item.id && ids.add(item.id)));
    return ids;
  }, [duplicates]);

  if (!token) return <Navigate to="/mod/login" replace />;

  const action = async (item, kind) => {
    if (kind === "delete" && !window.confirm(`Ta bort Bygdepuls-posten "${item.title || ""}"? Den mjukraderas.`)) return;
    try {
      if (kind === "delete") {
        await axios.delete(`${API}/mod/bygdepuls/${item.id}`, { headers: headers() });
      } else {
        await axios.post(`${API}/mod/bygdepuls/${item.id}/${kind}`, {}, { headers: headers() });
      }
      await load();
    } catch (err) {
      toast.error(`Åtgärden misslyckades: ${err.response?.data?.detail || err.message || "fel"}`);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="mod-bygdepuls-page">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <Link to="/mod" className="inline-flex items-center gap-1.5 text-sm text-pine hover:underline mb-2"><ArrowLeft size={14}/> Till moderatorpanelen</Link>
          <h1 className="font-fraktur-smooth text-3xl text-falu">Bygdepuls</h1>
          <p className="text-sm text-bark mt-1">Samma moderationslivscykel som övrigt innehåll: publicera, avpublicera, arkivera och mjukradera.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 border border-pine text-pine px-3 py-2 rounded-sm text-sm font-semibold"><RefreshCw size={14}/> Uppdatera</button>
      </div>

      {duplicates.length > 0 && (
        <div className="mb-5 border border-amber-400 bg-amber-50 p-4 rounded-sm" data-testid="bygdepuls-duplicate-warning">
          <div className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle size={16}/> {duplicates.length} möjlig{duplicates.length === 1 ? "" : "a"} dublettgrupp{duplicates.length === 1 ? "" : "er"}</div>
          <p className="text-sm text-amber-900/80 mt-1">Datumsavvikelser räknas inte som identitet. Kontrollera posterna nedan och behåll endast den kanoniska posten.</p>
        </div>
      )}

      {loading ? <p className="text-sm text-bark">Laddar…</p> : (
        <div className="space-y-3">
          {items.map((item) => {
            const duplicate = duplicateIds.has(item.id);
            const published = item.status === "Publicerat";
            return (
              <article key={item.id} className={`border rounded-sm p-4 ${duplicate ? "border-amber-400 bg-amber-50/40" : "border-warm-border bg-white"}`} data-testid={`bygdepuls-item-${item.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2 items-center mb-1">
                      <h2 className="font-heading text-lg text-ink">{item.title || "Utan titel"}</h2>
                      <span className="text-[11px] px-2 py-0.5 border rounded-full">{item.status || "okänd"}</span>
                      {duplicate && <span className="text-[11px] px-2 py-0.5 border border-amber-400 text-amber-900 rounded-full">Dublettkandidat</span>}
                    </div>
                    <p className="text-sm text-bark leading-relaxed">{item.summary || ""}</p>
                    <p className="text-xs text-bark/60 mt-2">{[item.locality, item.fact_date, item.source_name && `Källa: ${item.source_name}`].filter(Boolean).join(" · ")}</p>
                    <p className="text-[11px] text-bark/40 mt-1 break-all">ID: {item.id}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {published ? (
                      <button onClick={() => action(item, "avpublicera")} className="inline-flex items-center gap-1 text-xs border border-falu text-falu px-2.5 py-1.5 rounded-sm"><EyeOff size={13}/> Avpublicera</button>
                    ) : (
                      <button onClick={() => action(item, "publicera")} className="inline-flex items-center gap-1 text-xs border border-pine text-pine px-2.5 py-1.5 rounded-sm"><Eye size={13}/> Publicera</button>
                    )}
                    <button onClick={() => action(item, "arkivera")} className="inline-flex items-center gap-1 text-xs border border-warm-border text-bark px-2.5 py-1.5 rounded-sm"><Archive size={13}/> Arkivera</button>
                    <button onClick={() => action(item, "delete")} className="inline-flex items-center gap-1 text-xs border border-falu/50 text-falu px-2.5 py-1.5 rounded-sm"><Trash2 size={13}/> Ta bort</button>
                  </div>
                </div>
              </article>
            );
          })}
          {items.length === 0 && <p className="text-sm text-bark">Inga Bygdepuls-poster hittades.</p>}
        </div>
      )}
    </div>
  );
}
